'use client';

import { useState } from 'react';
import type { ExternalNetworkDto } from '@terra/shared-contracts-external-network';
import {
  type LinkableWebApplication,
  WebAppDetectionSettingsSection,
} from '@terra/ui-design-system';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useApplications } from '../../../../hooks/useTargets';
import { useUpdateExternalNetworkSurface } from '../../../../hooks/api/useSurfaceSettings';
import {
  useConfirmExternalNetworkDetectWebApps,
  useExternalNetworkWebAppClusters,
  usePreviewExternalNetworkWebAppDrafts,
} from '../../../../hooks/api/useExternalNetworkWebApps';
import { useCompanyId } from '../../../../store/projectStore';
import { TARGET_TYPE } from '../../../../utils/target-scope.utils';

export function ExternalWebAppsSettingsPanel({
  surface,
  surfaceId,
}: {
  surface: Pick<
    ExternalNetworkDto,
    'detectWebAppsEnabled' | 'webAppDraftStrategy'
  >;
  surfaceId: string;
}) {
  const { canManageApplications } = usePermissions();
  const companyId = useCompanyId();
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const updateSurface = useUpdateExternalNetworkSurface(surfaceId);
  const confirmDetect = useConfirmExternalNetworkDetectWebApps(surfaceId);
  const preview = usePreviewExternalNetworkWebAppDrafts({
    surfaceId,
    strategy: surface.webAppDraftStrategy,
    enabled: isReviewOpen || !surface.detectWebAppsEnabled,
  });
  const persistedClusters = useExternalNetworkWebAppClusters({ surfaceId });
  const { applications } = useApplications({ companyId });
  const linkableApplications: LinkableWebApplication[] = applications
    .filter((target) => target.targetType === TARGET_TYPE.WEB_APPLICATION)
    .map((target) => ({ id: target.id, name: target.name, url: target.url }));

  return (
    <WebAppDetectionSettingsSection
      detectWebAppsEnabled={surface.detectWebAppsEnabled}
      webAppDraftStrategy={surface.webAppDraftStrategy}
      previewClusters={preview.data?.clusters}
      persistedClusters={persistedClusters.data?.clusters}
      clustersLoading={preview.isLoading || persistedClusters.isLoading}
      previewFailed={preview.isError}
      applications={linkableApplications}
      isReadOnly={!canManageApplications}
      isUpdatingSurface={updateSurface.isPending}
      isConfirming={confirmDetect.isPending}
      onReviewOpenChange={setIsReviewOpen}
      onUpdateSurface={(dto) => updateSurface.mutateAsync(dto)}
      onConfirm={(body) => confirmDetect.mutateAsync(body)}
    />
  );
}
